/**
 * Browser-bound OAuth sign-in in a popup — provider-neutral (items 65A, 66).
 *
 * The other half of backend/shared/services/oauth_popup.py. The public
 * callback finishes nothing: it forwards the service's answer to a landing
 * page of ours, which hands it over a same-origin BroadcastChannel to the
 * window that started the sign-in — the one window holding that `state`.
 * That window acknowledges it, posts it to its flow's authenticated finish
 * route and says how it ended. A sign-in link opened in someone else's
 * browser lands where nobody listens: nothing is exchanged, nothing stored.
 *
 * Never `window.opener` / `popup.closed`: a service's Cross-Origin-Opener-
 * Policy may cut the link, and then `closed` reads true while the person is
 * still signing in. A wait ends on the answer, on its time limit, or when
 * the caller cancels it.
 *
 * A connection made in the same tab (the page goes to the provider and comes
 * back) is bound by that tab's sessionStorage instead: `rememberSameTabOAuth`
 * before leaving, `claimSameTabOAuth` on the way back.
 *
 * What makes a link opened by someone else harmless is the finish route: it
 * takes a state only from the person who started it, in that organization,
 * fresh. So a page holding the person's own session may finish an answer no
 * window took (`finishOAuthHere`, item 88b): a tab loaded before item 66
 * started it and speaks the old protocol. A consent popup carries a copy of
 * its opener's sessionStorage (measured: Chromium, Firefox, WebKit, also
 * across a provider page whose Cross-Origin-Opener-Policy cuts the opener);
 * a link opened in another tab or browser carries none, and finishes nothing.
 *
 * Used by the connector library's per-person sign-in (myConnectorsService)
 * and by Google Drive / Dropbox (googleAccessService, useCloudConnections).
 */

export const OAUTH_POPUP_CHANNEL = 'corpbrain-oauth-popup';

/** What the service sent back — the fields the finish route takes. */
export interface OAuthCallback {
  state: string;
  code: string | null;
  error: string | null;
  iss: string | null;
}

/** How the starting window says it ended: its flow's outcome, plus what the landing page may show. */
export interface OAuthOutcome {
  outcome: string;
  detail?: Record<string, string>;
}

type Message =
  | { type: 'oauth-callback'; callback: OAuthCallback }
  | { type: 'oauth-received'; state: string }
  | { type: 'oauth-outcome'; state: string; result: OAuthOutcome };

function channel(): BroadcastChannel | null {
  return typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(OAUTH_POPUP_CHANNEL) : null;
}

/** A wait the caller can stop: resolves null when stopped or out of time. */
export interface Waiting<T> {
  result: Promise<T | null>;
  cancel: () => void;
}

/** Listen on the channel until `pick` returns an answer, the limit passes, or cancel. */
function listen<T>(pick: (m: Message, ch: BroadcastChannel) => T | undefined, limitMs: number,
  onOpen?: (ch: BroadcastChannel) => void): Waiting<T> {
  const ch = channel();
  if (!ch) return { result: Promise.resolve(null), cancel: () => {} };
  let settle: (v: T | null) => void = () => {};
  const result = new Promise<T | null>((resolve) => { settle = resolve; });
  let done = false;
  const end = (v: T | null) => {
    if (done) return;
    done = true;
    window.clearTimeout(timer);
    ch.close();
    settle(v);
  };
  const timer = window.setTimeout(() => end(null), limitMs);
  ch.onmessage = (e: MessageEvent<Message>) => {
    if (!e.data) return;
    const v = pick(e.data, ch);
    if (v !== undefined) end(v);
  };
  onOpen?.(ch);
  return { result, cancel: () => end(null) };
}

/**
 * Open the popup. MUST run in the click handler itself, before any await:
 * a window opened later is blocked. Point it at the service once the
 * authorization URL is known.
 */
export function openOAuthPopup(name: string): Window | null {
  return window.open('about:blank', name, 'popup,width=560,height=720');
}

/** In the window that started: wait for the answer to ITS state only, and acknowledge it. */
export function awaitOAuthCallback(state: string, limitMs: number): Waiting<OAuthCallback> {
  return listen<OAuthCallback>((m, ch) => {
    if (m.type !== 'oauth-callback' || m.callback?.state !== state) return undefined;
    ch.postMessage({ type: 'oauth-received', state } satisfies Message);
    return m.callback;
  }, limitMs);
}

/** In the window that started: tell the landing page how it ended, so it can say so. */
export function reportOAuthOutcome(state: string, result: OAuthOutcome): void {
  const ch = channel();
  if (!ch) return;
  ch.postMessage({ type: 'oauth-outcome', state, result } satisfies Message);
  ch.close();
}

/**
 * A whole popup sign-in, for any flow: start (the authorization URL and the
 * state), wait for the answer to that state, finish it, tell the landing
 * page. `popup` must already be open (see openOAuthPopup). The outcome is
 * the flow's own, 'cancelled' (stopped) or 'timeout' (no answer in
 * `limitMs`); a failed start closes the popup and rejects.
 */
export function runOAuthPopup(popup: Window, flow: {
  start: () => Promise<{ authorization_url: string; state: string }>;
  finish: (callback: OAuthCallback) => Promise<OAuthOutcome>;
  limitMs: number;
}): { outcome: Promise<string>; cancel: () => void } {
  let stop: () => void = () => {};
  let cancelled = false;
  const close = () => { try { popup.close(); } catch { /* severed by COOP — the page closes itself */ } };
  const outcome = (async (): Promise<string> => {
    let started: { authorization_url: string; state: string };
    try {
      started = await flow.start();
    } catch (err) {
      close();
      throw err;
    }
    if (cancelled) { close(); return 'cancelled'; }
    const wait = awaitOAuthCallback(started.state, flow.limitMs);
    stop = wait.cancel;
    popup.location.href = started.authorization_url;
    const callback = await wait.result;
    if (!callback) { close(); return cancelled ? 'cancelled' : 'timeout'; }
    let result: OAuthOutcome = { outcome: 'error' };
    try {
      result = await flow.finish(callback);
    } finally {
      reportOAuthOutcome(started.state, result);
    }
    return result.outcome;
  })();
  return { outcome, cancel: () => { cancelled = true; stop(); } };
}

const SAME_TAB_PREFIX = 'oauth-same-tab:';

/** Before this tab goes to the provider: note the state, with the one value finishing needs. */
export function rememberSameTabOAuth(state: string, value: string): void {
  sessionStorage.setItem(SAME_TAB_PREFIX + state, value);
}

/** Back from the provider: the value noted for this state — once — or null
 *  when this tab did not start it (the link was opened somewhere else). */
export function claimSameTabOAuth(state: string): string | null {
  const value = sessionStorage.getItem(SAME_TAB_PREFIX + state);
  if (value !== null) sessionStorage.removeItem(SAME_TAB_PREFIX + state);
  return value;
}

/** Back from the provider: the value this tab noted for the state, else the
 *  callback's own word for it when it is one of `known` — a tab loaded before
 *  item 66 left without noting anything (item 88b). Null: nothing to finish. */
export function claimSameTabOAuthOr(state: string, named: string | null, known: readonly string[]): string | null {
  return claimSameTabOAuth(state) ?? (named !== null && known.includes(named) ? named : null);
}

/** The headers of THIS tab's own sign-in (as services/api.ts sends them), or
 *  null when the tab has none — a link opened in another tab or browser. */
export function tabSessionHeaders(): Record<string, string> | null {
  const token = sessionStorage.getItem('access_token');
  if (!token) return null;
  let tenantId = sessionStorage.getItem('tenant_id');
  if (!tenantId) {
    try { tenantId = JSON.parse(sessionStorage.getItem('user') || 'null')?.tenant_id ?? null; } catch { tenantId = null; }
  }
  return { Authorization: `Bearer ${token}`, ...(tenantId ? { 'X-Tenant-ID': String(tenantId) } : {}) };
}

/** A finish route's answer: the flow's outcome and, for Google, the level the state asked for. */
export interface FinishAnswer {
  outcome: string;
  access?: string;
}

/**
 * On the landing page, when no window of this browser took the answer
 * (item 88b): finish it here, as this tab's own person — or null, quickly,
 * when this tab has no session or its session is over (then nothing was
 * connected, as before). The finish route still refuses any state this
 * person did not start. A plain request, not services/api.ts: a session that
 * ended must not send a popup to the sign-in page.
 */
export async function finishOAuthHere(finishUrl: string, callback: OAuthCallback): Promise<FinishAnswer | null> {
  const headers = tabSessionHeaders();
  if (!headers) return null;
  try {
    const res = await fetch(finishUrl, {
      method: 'POST',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify(callback),
    });
    if (res.status === 401 || res.status === 403) return null;
    if (!res.ok) return { outcome: 'error' };
    const data = await res.json();
    return {
      outcome: typeof data?.outcome === 'string' ? data.outcome : 'error',
      ...(typeof data?.access === 'string' ? { access: data.access } : {}),
    };
  } catch {
    return { outcome: 'error' };
  }
}

/** On the landing page: the service's answer from this page's address, or null. */
export function readOAuthCallback(search: string): OAuthCallback | null {
  const params = new URLSearchParams(search);
  const state = params.get('state');
  if (!state) return null;
  return { state, code: params.get('code'), error: params.get('error'), iss: params.get('iss') };
}

/** No window acknowledged the answer this quickly: none of this browser is
 *  waiting for it (generous: a background tab may answer late). */
const ACK_MS = 8000;

/**
 * On the landing page: hand the answer to the window that started, and wait
 * for how it ended. When no window of this browser took it, quickly: the
 * flow's `finishHere` (item 88b — this page's own session, see
 * `finishOAuthHere`), else null (the link was opened somewhere else).
 * Otherwise up to `waitMs` for the finish; a window that took it is never
 * finished for a second time here, nor is a hand-over that was cancelled.
 */
export function handOverOAuthCallback(callback: OAuthCallback, waitMs: number,
  finishHere?: (callback: OAuthCallback) => Promise<OAuthOutcome | null>): Waiting<OAuthOutcome> {
  let acked = false;
  let cancelled = false;
  let ackTimer = 0;
  const waiting = listen<OAuthOutcome>((m) => {
    if (m.type === 'oauth-received' && m.state === callback.state) { acked = true; return undefined; }
    if (m.type === 'oauth-outcome' && m.state === callback.state && m.result) return m.result;
    return undefined;
  }, waitMs, (ch) => {
    ch.postMessage({ type: 'oauth-callback', callback } satisfies Message);
    ackTimer = window.setTimeout(() => { if (!acked) waiting.cancel(); }, ACK_MS);
  });
  const result = waiting.result.then((handed) => {
    window.clearTimeout(ackTimer);
    if (handed !== null || acked || cancelled || !finishHere) return handed;
    return finishHere(callback);
  });
  return { result, cancel: () => { cancelled = true; waiting.cancel(); } };
}
